import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

/** Changement de mot de passe, avec son propre bouton : rien à voir avec
 *  l'enregistrement des réglages. */
export default function CarteMotDePasse({ className = "" }) {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [actuel, setActuel] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirme, setConfirme] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const pretA = actuel.length > 0 && nouveau.length >= 8 && nouveau === confirme;

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await api.post("/compte/motdepasse", { actuel, nouveau });
      toast.success(fr ? "Mot de passe changé" : "Password changed");
      setActuel(""); setNouveau(""); setConfirme("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || (fr ? "Échec" : "Failed"));
    } finally {
      setEnvoi(false);
    }
  };

  const champ = "mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500";
  const etiquette = "text-xs uppercase tracking-widest text-ink-soft";

  return (
    <form onSubmit={soumettre} className={className}>
      <h3 className="font-heading font-bold mb-1 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-brand-ink" />
        {fr ? "Mot de passe" : "Password"}
      </h3>
      <p className="text-xs text-ink-faint mb-5">
        {fr
          ? "Vos autres appareils seront déconnectés."
          : "Your other devices will be signed out."}
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className={etiquette}>{fr ? "Actuel" : "Current"}</Label>
          <Input data-testid="mdp-actuel" type="password" autoComplete="current-password"
                 value={actuel} onChange={(e) => setActuel(e.target.value)} className={champ} />
        </div>
        <div>
          <Label className={etiquette}>{fr ? "Nouveau" : "New"}</Label>
          <Input data-testid="mdp-nouveau" type="password" autoComplete="new-password" minLength={8}
                 value={nouveau} onChange={(e) => setNouveau(e.target.value)} className={champ} />
        </div>
        <div>
          <Label className={etiquette}>{fr ? "Confirmer" : "Confirm"}</Label>
          <Input data-testid="mdp-confirme" type="password" autoComplete="new-password"
                 value={confirme} onChange={(e) => setConfirme(e.target.value)} className={champ} />
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4">
        <Button type="submit" disabled={!pretA || envoi} data-testid="mdp-enregistrer"
                className="bg-hover-strong hover:bg-line-strong text-ink rounded-md font-semibold">
          {envoi && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {fr ? "Changer le mot de passe" : "Change password"}
        </Button>
        {nouveau.length > 0 && nouveau.length < 8 && (
          <span className="text-xs text-ink-faint">{fr ? "8 caractères minimum." : "8 characters minimum."}</span>
        )}
        {confirme.length > 0 && nouveau !== confirme && (
          <span className="text-xs text-red-400">{fr ? "Les deux ne correspondent pas." : "The two don't match."}</span>
        )}
      </div>
    </form>
  );
}
